import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  encryptSecret,
  decryptSecret,
  copyWithAutoClear,
  generatePassword,
} from '../lib/vaultCrypto';
import { playSound } from '../lib/soundEffects';
import { startVoiceDictation, stopVoiceDictation } from '../lib/voiceDictation';
import { parseCredentialRecord, formatCredentialRecord } from '../lib/credentialParser';

const {
  FiX,
  FiFileText,
  FiLock,
  FiSave,
  FiEye,
  FiEyeOff,
  FiCopy,
  FiCheck,
  FiLoader,
  FiCpu,
  FiKey,
  FiShield,
  FiMic,
  FiMicOff,
  FiEdit3,
  FiFolder,
} = FiIcons;

export default function BookmarkInfoPanel({
  item,
  themeColors: tc,
  isDark = false,
  onSave,
  onClose,
  onAISummary,
  onMoveTo,
  userId = 'default_user',
}) {
  const [tab, setTab] = useState('note');
  const [note, setNote] = useState('');
  const [credentials, setCredentials] = useState('');
  const [showCreds, setShowCreds] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [editingCreds, setEditingCreds] = useState(false);
  const [copiedField, setCopiedField] = useState('');
  const [saving, setSaving] = useState(false);
  const [isDictating, setIsDictating] = useState(false);

  // Structured credential fields for editing
  const [credForm, setCredForm] = useState({
    username: '',
    email: '',
    password: '',
    activationCode: '',
    notes: '',
  });

  useEffect(() => {
    return () => {
      stopVoiceDictation();
    };
  }, []);

  const toggleDictation = () => {
    if (isDictating) {
      stopVoiceDictation();
      setIsDictating(false);
      playSound('blip');
    } else {
      playSound('launch');
      setEditingNote(true);
      const rec = startVoiceDictation({
        onResult: ({ final }) => {
          if (final) {
            setNote((prev) => (prev ? prev.trim() + ' ' + final : final));
          }
        },
        onError: (err) => {
          console.warn('[Voice Dictation Error]', err);
          setIsDictating(false);
        },
        onEnd: () => {
          setIsDictating(false);
        },
      });
      if (rec) {
        setIsDictating(true);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (item) {
      setNote(item.note || '');
      setEditingNote(false);
      setEditingCreds(false);
      setShowCreds(false);
      setTab('note');

      // Zero-Knowledge Decryption into memory
      if (item.credentials) {
        decryptSecret(item.credentials, userId).then((dec) => {
          if (!cancelled) {
            setCredentials(dec);
            const parsed = parseCredentialRecord(dec);
            setCredForm({
              username: parsed.username || '',
              email: parsed.email || '',
              password: parsed.password || '',
              activationCode: parsed.activationCode || '',
              notes: parsed.notes || '',
            });
          }
        });
      } else {
        setCredentials('');
        setCredForm({
          username: '',
          email: '',
          password: '',
          activationCode: '',
          notes: '',
        });
      }
    }
    return () => { cancelled = true; };
  }, [item?.id, item?.credentials, userId]);

  const parsedCreds = React.useMemo(() => {
    return parseCredentialRecord(credentials);
  }, [credentials]);

  const handleSave = async () => {
    setSaving(true);
    playSound('lock');

    let credsToEncrypt = credentials;
    if (editingCreds) {
      credsToEncrypt = formatCredentialRecord({
        email: credForm.email,
        username: credForm.username || credForm.email,
        password: credForm.password,
        activationCode: credForm.activationCode,
        notes: credForm.notes,
      });
      setCredentials(credsToEncrypt);
    }

    const cipherCredentials = credsToEncrypt.trim()
      ? await encryptSecret(credsToEncrypt.trim(), userId)
      : '';

    await onSave(item.id, { note, credentials: cipherCredentials });
    setSaving(false);
    setEditingNote(false);
    setEditingCreds(false);
  };

  const handleCopy = (text, fieldName = 'text') => {
    playSound('copy');
    setCopiedField(fieldName);
    copyWithAutoClear(text, 30, () => {});
    setTimeout(() => {
      setCopiedField((curr) => (curr === fieldName ? '' : curr));
    }, 2000);
  };

  const handleGeneratePassword = () => {
    playSound('click');
    const pwd = generatePassword({ length: 18 });
    setCredForm((prev) => ({ ...prev, password: pwd }));
  };

  const panelBg = isDark ? 'rgba(12,16,14,0.97)' : 'rgba(255,255,255,0.98)';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
  const fieldBorder = isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0';
  const hasNote = note?.trim();
  const hasCreds = credentials?.trim();

  const tabs = [
    { id: 'note', label: 'Note', icon: FiFileText, hasContent: !!hasNote },
    { id: 'credentials', label: 'Credentials', icon: FiLock, hasContent: !!hasCreds },
  ];

  return (
    <AnimatePresence>
      {item && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[55] bg-black/40 backdrop-blur-sm"
          />

          {/* Responsive Viewport Centering Container */}
          <div className="fixed inset-0 z-[56] flex items-center justify-center md:justify-end md:pr-8 p-3 sm:p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 16 }}
              transition={{ type: 'spring', stiffness: 420, damping: 30 }}
              className="pointer-events-auto w-full max-w-[380px] max-h-[min(560px,calc(100dvh-4rem))] flex flex-col rounded-3xl shadow-2xl overflow-hidden"
              style={{
                background: panelBg,
                border: `1px solid ${tc.border}`,
                backdropFilter: 'blur(28px)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
            {/* Header */}
            <div
              className="flex items-center justify-between px-4 pt-4 pb-3 border-b flex-shrink-0"
              style={{ borderColor: tc.border }}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                {item.logoUrl && (
                  <img
                    src={item.logoUrl}
                    alt=""
                    className="w-6 h-6 rounded-md object-contain flex-shrink-0"
                    onError={(e) => (e.target.style.display = 'none')}
                  />
                )}
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: tc.text }}>
                    {item.title}
                  </p>
                  <p className="text-[10px] font-mono opacity-60 truncate" style={{ color: tc.muted }}>
                    {item.url}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1.5 flex-shrink-0">
                {onMoveTo && (
                  <button
                    onClick={() => { onMoveTo(item); onClose(); }}
                    className="flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-lg border transition-all hover:scale-105 active:scale-95"
                    style={{
                      borderColor: 'rgba(255,255,255,0.15)',
                      backgroundColor: 'rgba(255,255,255,0.06)',
                      color: tc.text || '#fff',
                    }}
                    title="Move to another folder across the app"
                  >
                    <SafeIcon icon={FiFolder} className="w-3 h-3 text-emerald-400" />
                    <span>Move</span>
                  </button>
                )}
                {onAISummary && (
                  <button
                    onClick={() => onAISummary(item)}
                    className="flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-lg border transition-all hover:scale-105 active:scale-95"
                    style={{
                      borderColor: `${tc.accent}35`,
                      backgroundColor: `${tc.accent}15`,
                      color: tc.accent,
                    }}
                    title="Generate AI summary of this website"
                  >
                    <SafeIcon icon={FiCpu} className="w-3 h-3" />
                    <span>AI Summary</span>
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-1 rounded-lg hover:bg-white/10 transition-colors"
                  style={{ color: tc.muted }}
                >
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Tabs (Note & Credentials only) */}
            <div className="flex px-4 pt-3 gap-2 flex-shrink-0">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => {
                    playSound('click');
                    setTab(t.id);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all relative cursor-pointer"
                  style={
                    tab === t.id
                      ? {
                          backgroundColor: `${tc.accent}20`,
                          color: tc.accent,
                          border: `1px solid ${tc.accent}50`,
                        }
                      : { color: tc.muted, border: '1px solid transparent' }
                  }
                >
                  <SafeIcon icon={t.icon} className="w-3 h-3" />
                  {t.label}
                  {t.hasContent && (
                    <span
                      className="w-1.5 h-1.5 rounded-full absolute -top-0.5 -right-0.5"
                      style={{ backgroundColor: tc.accent }}
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Content Body */}
            <div className="flex-1 overflow-y-auto px-4 py-3" style={{ scrollbarWidth: 'none' }}>
              <AnimatePresence mode="wait">
                {/* NOTE TAB */}
                {tab === 'note' && (
                  <motion.div
                    key="note"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <button
                        type="button"
                        onClick={toggleDictation}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all ${
                          isDictating
                            ? 'bg-rose-500/20 text-rose-300 border-rose-500/50 animate-pulse'
                            : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20'
                        }`}
                        title="Voice Dictation: Speak to automatically dictate notes"
                      >
                        <SafeIcon icon={isDictating ? FiMicOff : FiMic} className="w-3.5 h-3.5" />
                        <span>{isDictating ? '🎙️ Listening...' : '🎙️ Dictate Note'}</span>
                      </button>
                      {hasNote && !editingNote && (
                        <button
                          type="button"
                          onClick={() => setEditingNote(true)}
                          className="text-[11px] font-medium flex items-center gap-1 opacity-70 hover:opacity-100"
                          style={{ color: tc.accent }}
                        >
                          <SafeIcon icon={FiEdit3} className="w-3 h-3" /> Edit
                        </button>
                      )}
                    </div>

                    {editingNote ? (
                      <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Write a markdown note for this bookmark…"
                        autoFocus
                        rows={7}
                        className="w-full text-sm rounded-xl p-3 resize-none focus:outline-none border transition-colors"
                        style={{ backgroundColor: inputBg, color: tc.text, borderColor: tc.border }}
                      />
                    ) : (
                      <div
                        onClick={() => setEditingNote(true)}
                        className="w-full min-h-[140px] text-sm rounded-xl p-3 border cursor-text transition-colors"
                        style={{
                          backgroundColor: inputBg,
                          color: hasNote ? tc.text : tc.muted,
                          borderColor: tc.border,
                        }}
                      >
                        {hasNote ? (
                          <div className="whitespace-pre-wrap leading-relaxed">{note}</div>
                        ) : (
                          <span className="italic">Click to add markdown notes or quotes…</span>
                        )}
                      </div>
                    )}

                    {editingNote && (
                      <button
                        onClick={() => setEditingNote(false)}
                        className="mt-1 text-[11px] underline opacity-60 hover:opacity-100"
                        style={{ color: tc.muted }}
                      >
                        Cancel
                      </button>
                    )}
                  </motion.div>
                )}

                {/* CREDENTIALS TAB */}
                {tab === 'credentials' && (
                  <motion.div
                    key="creds"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    className="space-y-3"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                          <SafeIcon icon={FiShield} className="w-2.5 h-2.5" />
                          AES-256
                        </span>
                        <p className="text-[11px]" style={{ color: tc.muted }}>
                          Encrypted Vault Record
                        </p>
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            if (!editingCreds) {
                              const p = parseCredentialRecord(credentials);
                              setCredForm({
                                username: p.username || '',
                                email: p.email || '',
                                password: p.password || '',
                                activationCode: p.activationCode || '',
                                notes: p.notes || '',
                              });
                            }
                            setEditingCreds(!editingCreds);
                          }}
                          className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-lg border font-medium transition-all"
                          style={{
                            borderColor: `${tc.accent}50`,
                            color: tc.accent,
                            backgroundColor: `${tc.accent}15`,
                          }}
                        >
                          <SafeIcon icon={editingCreds ? FiX : FiEdit3} className="w-3 h-3" />
                          <span>{editingCreds ? 'Done' : 'Edit'}</span>
                        </button>
                      </div>
                    </div>

                    {editingCreds ? (
                      /* Structured Credential Editor */
                      <div className="space-y-2.5">
                        <div>
                          <label className="block text-[11px] font-medium mb-1" style={{ color: tc.muted }}>
                            Email / Account Username
                          </label>
                          <input
                            type="text"
                            value={credForm.username}
                            onChange={(e) => setCredForm((p) => ({ ...p, username: e.target.value, email: e.target.value }))}
                            placeholder="user@example.com"
                            className="w-full text-xs rounded-xl px-3 py-2 border focus:outline-none transition-colors font-mono"
                            style={{ backgroundColor: inputBg, color: tc.text, borderColor: fieldBorder }}
                          />
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-[11px] font-medium" style={{ color: tc.muted }}>
                              Password
                            </label>
                            <button
                              type="button"
                              onClick={handleGeneratePassword}
                              className="text-[10px] font-semibold text-emerald-400 hover:underline flex items-center gap-1 cursor-pointer"
                            >
                              <SafeIcon icon={FiKey} className="w-2.5 h-2.5" /> 🎲 Generate
                            </button>
                          </div>
                          <div className="relative">
                            <input
                              type={showCreds ? 'text' : 'password'}
                              value={credForm.password}
                              onChange={(e) => setCredForm((p) => ({ ...p, password: e.target.value }))}
                              placeholder="••••••••••••"
                              className="w-full text-xs rounded-xl pl-3 pr-8 py-2 border focus:outline-none transition-colors font-mono"
                              style={{ backgroundColor: inputBg, color: tc.text, borderColor: fieldBorder }}
                            />
                            <button
                              type="button"
                              onClick={() => setShowCreds(!showCreds)}
                              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                            >
                              <SafeIcon icon={showCreds ? FiEyeOff : FiEye} className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="block text-[11px] font-medium mb-1" style={{ color: tc.muted }}>
                            Activation Code / 2FA / License (Optional)
                          </label>
                          <input
                            type="text"
                            value={credForm.activationCode}
                            onChange={(e) => setCredForm((p) => ({ ...p, activationCode: e.target.value }))}
                            placeholder="ACT-1234-ABCD or 2FA token"
                            className="w-full text-xs rounded-xl px-3 py-2 border focus:outline-none transition-colors font-mono"
                            style={{ backgroundColor: inputBg, color: tc.text, borderColor: fieldBorder }}
                          />
                        </div>
                      </div>
                    ) : (
                      /* Structured Credential Viewer */
                      <div className="space-y-2">
                        {hasCreds ? (
                          <>
                            {/* Email / Username Field */}
                            <div
                              className="p-2.5 rounded-xl border flex items-center justify-between gap-2"
                              style={{ backgroundColor: inputBg, borderColor: fieldBorder }}
                            >
                              <div className="min-w-0">
                                <span className="text-[10px] uppercase font-mono block opacity-60" style={{ color: tc.muted }}>
                                  Username / Email
                                </span>
                                <span className="text-xs font-mono font-medium truncate block" style={{ color: tc.text }}>
                                  {parsedCreds.username || parsedCreds.email || '—'}
                                </span>
                              </div>
                              {(parsedCreds.username || parsedCreds.email) && (
                                <button
                                  type="button"
                                  onClick={() => handleCopy(parsedCreds.username || parsedCreds.email, 'user')}
                                  className="p-1.5 rounded-lg border hover:bg-white/10 transition-all text-xs"
                                  style={{ borderColor: fieldBorder, color: tc.muted }}
                                  title="Copy username"
                                >
                                  <SafeIcon icon={copiedField === 'user' ? FiCheck : FiCopy} className="w-3 h-3 text-emerald-400" />
                                </button>
                              )}
                            </div>

                            {/* Password Field */}
                            <div
                              className="p-2.5 rounded-xl border flex items-center justify-between gap-2"
                              style={{ backgroundColor: inputBg, borderColor: fieldBorder }}
                            >
                              <div className="min-w-0">
                                <span className="text-[10px] uppercase font-mono block opacity-60" style={{ color: tc.muted }}>
                                  Password
                                </span>
                                <span className="text-xs font-mono font-medium tracking-wide truncate block" style={{ color: tc.text }}>
                                  {parsedCreds.password
                                    ? showCreds
                                      ? parsedCreds.password
                                      : '••••••••••••••••'
                                    : '—'}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                {parsedCreds.password && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => setShowCreds(!showCreds)}
                                      className="p-1.5 rounded-lg border hover:bg-white/10 transition-all text-xs"
                                      style={{ borderColor: fieldBorder, color: tc.muted }}
                                      title={showCreds ? 'Hide' : 'Reveal'}
                                    >
                                      <SafeIcon icon={showCreds ? FiEyeOff : FiEye} className="w-3 h-3" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleCopy(parsedCreds.password, 'pass')}
                                      className="p-1.5 rounded-lg border hover:bg-white/10 transition-all text-xs"
                                      style={{ borderColor: fieldBorder, color: tc.muted }}
                                      title="Copy password"
                                    >
                                      <SafeIcon icon={copiedField === 'pass' ? FiCheck : FiCopy} className="w-3 h-3 text-emerald-400" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Activation / 2FA Code Field (if present) */}
                            {parsedCreds.activationCode && (
                              <div
                                className="p-2.5 rounded-xl border flex items-center justify-between gap-2"
                                style={{ backgroundColor: inputBg, borderColor: fieldBorder }}
                              >
                                <div className="min-w-0">
                                  <span className="text-[10px] uppercase font-mono block text-amber-400">
                                    Activation / 2FA Code
                                  </span>
                                  <span className="text-xs font-mono font-semibold tracking-wider truncate block text-amber-300">
                                    {parsedCreds.activationCode}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleCopy(parsedCreds.activationCode, 'code')}
                                  className="p-1.5 rounded-lg border hover:bg-white/10 transition-all text-xs"
                                  style={{ borderColor: fieldBorder, color: tc.muted }}
                                  title="Copy activation code"
                                >
                                  <SafeIcon icon={copiedField === 'code' ? FiCheck : FiCopy} className="w-3 h-3 text-amber-400" />
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div
                            onClick={() => setEditingCreds(true)}
                            className="p-6 text-center border rounded-xl border-dashed cursor-pointer hover:border-emerald-500/50 transition-colors"
                            style={{ borderColor: fieldBorder }}
                          >
                            <SafeIcon icon={FiLock} className="w-6 h-6 mx-auto mb-1.5 opacity-40 text-emerald-400" />
                            <p className="text-xs font-medium" style={{ color: tc.text }}>No credentials stored</p>
                            <p className="text-[10px] mt-0.5" style={{ color: tc.muted }}>Click here to add Email, Password, or Activation Code</p>
                          </div>
                        )}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Save Button */}
            <div className="px-4 pb-4 flex-shrink-0">
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer shadow-lg shadow-emerald-500/20"
                style={{
                  backgroundColor: tc.accent,
                  color: '#000',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                <SafeIcon
                  icon={saving ? FiLoader : FiSave}
                  className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`}
                />
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}